/**
 * Writing a CSV cell that a spreadsheet will read as DATA.
 *
 * MIRRORED FILE — twin of `src/utils/csv.js`. See invariant 2 in CLAUDE.md and
 * the pair list in `src/__tests__/mirroredUtilParity.test.js`. Both sides of the
 * wire export accounting data, and a rule about what a spreadsheet EXECUTES must
 * not be true on one side and false on the other: five of the six copies this
 * replaces were client pages and the sixth was a server controller, all
 * exporting the same accounts data with the same hole.
 *
 * Two problems, one function
 * --------------------------
 * 1. QUOTING. The obvious half: a value containing a comma, a quote or a
 *    newline has to be quoted and its quotes doubled, or the row silently gains
 *    a column. All six private copies got this half right.
 *
 * 2. FORMULAS. The half none of them did. A cell whose text begins `=`, `+`,
 *    `-` or `@` is a FORMULA to Excel, LibreOffice and Google Sheets — quoting
 *    does not change that, because the quotes are consumed by the CSV parser
 *    before the formula parser ever sees the text. A supplier name, a payment
 *    reference or an invoice note is attacker-influenced text that arrives here
 *    from an emailed PDF and leaves as a file a person opens on their own
 *    machine. `=HYPERLINK(...)` and `=WEBSERVICE(...)` exfiltrate the row on
 *    open; `=cmd|'/c calc'!A0` is the DDE variant.
 *    The mitigation is the standard one: prefix the cell with a single
 *    apostrophe, which every one of those applications reads as "the rest of
 *    this cell is text" and does not display.
 *
 * WHY `-` IS NOT SIMPLY NEUTRALIZED, which is the subtle part.
 * `-200.00` is an ordinary figure on an accounts export, and prefixing it would
 * turn every credit and every negative variance into a text cell — the column
 * would stop summing, on a financial report, which is a worse bug than the one
 * being fixed. So a value that is a plain number is left exactly as it is, and
 * the neutralization applies only to text. `=`, `+` and `@` are never the first
 * character of a number this application produces, but they go through the same
 * check for the same reason.
 */

/** Leading characters a spreadsheet treats as the start of a formula. */
export const FORMULA_LEAD = /^[=+\-@\t\r]/;

/**
 * A plain number, including a leading minus and a leading `+`.
 * Deliberately strict: no thousands separators, no currency symbol, no
 * exponent. Anything richer than this is text, and text gets neutralized.
 */
export const PLAIN_NUMBER = /^[+-]?\d+(\.\d+)?$/;

/**
 * One CSV cell: formula-neutralized if it needs it, then quoted.
 *
 * Always quoted, even when the text contains nothing that requires it. Four of
 * the six copies this replaces quoted unconditionally and one did not; quoting
 * always is the version that cannot be got wrong, and no reader minds.
 */
export function csvEscape(value: unknown): string {
  const text = value === null || value === undefined ? '' : String(value);
  const safe = FORMULA_LEAD.test(text) && !PLAIN_NUMBER.test(text) ? `'${text}` : text;
  return `"${safe.replace(/"/g, '""')}"`;
}

/**
 * Rows of cells to a CSV document. Rows are arrays; the caller decides whether
 * the first one is a header.
 *
 * CRLF between rows, because that is what RFC 4180 says and what Excel expects
 * when a quoted cell itself contains a newline.
 */
export function toCsv(rows: Array<Array<unknown>>): string {
  return (rows || []).map((row) => (row || []).map(csvEscape).join(',')).join('\r\n');
}

/**
 * Objects to a CSV document, taking the column order from `headers` — or from
 * the first row's own keys when no headers are given.
 *
 * Returns an empty string when there is nothing to export, which is what every
 * caller's `if (!rows.length) return;` guard used to say for itself.
 */
export function objectsToCsv(
  rows: Array<Record<string, unknown>>,
  headers?: string[]
): string {
  const list = Array.isArray(rows) ? rows : [];
  if (!list.length) return '';
  const cols = headers && headers.length ? headers : Object.keys(list[0]);
  return toCsv([cols, ...list.map((row) => cols.map((key) => row[key]))]);
}